import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import isEmpty from 'lodash/isEmpty';
import { Loader, PLATFORM_MODULES_LOGO, Steps } from '@thinknet-gmbh/platform-common-ui';

import { getStepsData, getValidFormData } from './questionnaire-page.helpers';
import { useGetEmployeeQuestionnaireSettingById } from 'src/hooks/employee-questionnaire-settings/get-employee-questionnaire-settings-by-id.hook';

import { QuestionnaireForm } from './components/questionnaire-form';

import useBreakpoint from 'antd/es/grid/hooks/useBreakpoint';

import { QUESTIONNAIRE_FORM_STEPS } from './questionnaire-page.constants';

import {
  IEmployeeQuestionnaire,
  IQuestionnaireFormData,
} from 'src/pages/auth/sub-pages/questionnaire/questionnaire.interfaces';

import {
  MainFormWrapper,
  LogoWrapper,
  MainWrapper,
  StepsWrapper,
  GreyLine,
  StepsContainer,
} from './questionnaire-page.styles';

const QuestionnairePageComponent = ({
  data,
  onSubmit,
  questionnaireData,
  handleDataSet,
}: {
  onSubmit: (values: IQuestionnaireFormData) => void;
  data: IQuestionnaireFormData;
  questionnaireData: IEmployeeQuestionnaire;
  handleDataSet: (values: IQuestionnaireFormData) => void;
}) => {
  const { t } = useTranslation('employee');
  const [currentStep, setCurrentStep] = useState(0);

  const screens = useBreakpoint();

  const handleValues = (isLastStep: boolean) => (values: IQuestionnaireFormData) => {
    handleDataSet(values);
    if (!isLastStep) {
      setCurrentStep(currentStep + 1);
    }
    if (isLastStep) {
      onSubmit({ ...data, ...values });
    }
  };

  const { questionnaireSettingsId = '' } = questionnaireData || {};

  const { questionnaireSetting, loading } = useGetEmployeeQuestionnaireSettingById(
    questionnaireSettingsId
  );

  const handleStepBack = (step: number) => () => {
    if (step > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const ModuleLogo = PLATFORM_MODULES_LOGO.YUU_HR_MODULE;

  const {
    employeePersonalInfo = [],
    emergencyContact = [],
    educationAndSkills = [],
    paymentAndInsurance = [],
    finishInformation = [],
  } = getValidFormData(questionnaireSetting?.settings || []) || {};

  const { steps, totalStepsCount } = getStepsData({
    educationAndSkills,
    paymentAndInsurance,
    emergencyContact,
    employeePersonalInfo,
    finishInformation,
  });

  const isLastStep = currentStep === totalStepsCount;

  const isFirstStep = currentStep === 0;

  if (loading) {
    return <Loader />;
  }

  return (
    <MainWrapper>
      <StepsWrapper>
        <LogoWrapper>
          <ModuleLogo />
        </LogoWrapper>
        <StepsContainer>
          <Steps current={currentStep} direction={screens.sm ? 'vertical' : 'horizontal'}>
            {steps.map((el, index) => (
              <Steps.Step
                key={el}
                {...(screens.sm && { title: t(`employee:questionnaire.labels.steps.${el}`) })}
              >
                {index + 1}
              </Steps.Step>
            ))}
          </Steps>
        </StepsContainer>
      </StepsWrapper>
      <GreyLine />
      <MainFormWrapper>
        {steps.map((step, index) => {
          return (
            <Fragment key={step}>
              {!isEmpty(employeePersonalInfo) &&
                index === currentStep &&
                step === QUESTIONNAIRE_FORM_STEPS.employeePersonalInfo && (
                  <QuestionnaireForm
                    onFinish={handleValues(isLastStep)}
                    currentStep={currentStep}
                    handleStepBack={handleStepBack(currentStep)}
                    isLastStep={isLastStep}
                    isFirstStep={isFirstStep}
                    initialValues={data}
                    fields={employeePersonalInfo}
                    formType={QUESTIONNAIRE_FORM_STEPS.employeePersonalInfo}
                  />
                )}
              {!isEmpty(emergencyContact) &&
                index === currentStep &&
                step === QUESTIONNAIRE_FORM_STEPS.emergencyContact && (
                  <QuestionnaireForm
                    onFinish={handleValues(isLastStep)}
                    currentStep={currentStep}
                    handleStepBack={handleStepBack(currentStep)}
                    isLastStep={isLastStep}
                    isFirstStep={isFirstStep}
                    initialValues={data}
                    fields={emergencyContact}
                    formType={QUESTIONNAIRE_FORM_STEPS.emergencyContact}
                  />
                )}
              {!isEmpty(educationAndSkills) &&
                index === currentStep &&
                step === QUESTIONNAIRE_FORM_STEPS.educationAndSkills && (
                  <QuestionnaireForm
                    onFinish={handleValues(isLastStep)}
                    currentStep={currentStep}
                    handleStepBack={handleStepBack(currentStep)}
                    isLastStep={isLastStep}
                    isFirstStep={isFirstStep}
                    initialValues={data}
                    fields={educationAndSkills}
                    formType={QUESTIONNAIRE_FORM_STEPS.educationAndSkills}
                  />
                )}
              {!isEmpty(paymentAndInsurance) &&
                index === currentStep &&
                step === QUESTIONNAIRE_FORM_STEPS.paymentAndInsurance && (
                  <QuestionnaireForm
                    onFinish={handleValues(isLastStep)}
                    currentStep={currentStep}
                    handleStepBack={handleStepBack(currentStep)}
                    isLastStep={isLastStep}
                    isFirstStep={isFirstStep}
                    initialValues={data}
                    fields={paymentAndInsurance}
                    formType={QUESTIONNAIRE_FORM_STEPS.paymentAndInsurance}
                  />
                )}
              {!isEmpty(finishInformation) &&
                index === currentStep &&
                step === QUESTIONNAIRE_FORM_STEPS.finishInformation && (
                  <QuestionnaireForm
                    onFinish={handleValues(isLastStep)}
                    currentStep={currentStep}
                    handleStepBack={handleStepBack(currentStep)}
                    isLastStep={isLastStep}
                    isFirstStep={isFirstStep}
                    initialValues={data}
                    fields={finishInformation}
                    questionnaireSetting={questionnaireSetting}
                    formType={QUESTIONNAIRE_FORM_STEPS.finishInformation}
                    isFinishStep
                  />
                )}
            </Fragment>
          );
        })}
      </MainFormWrapper>
    </MainWrapper>
  );
};

export default QuestionnairePageComponent;
